import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
  Chip,
  Paper,
  Divider,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import type {
  Weekday,
  TimeRange,
  ScheduleState,
} from '@/entities/campaign/types';
import { FormikProps } from '@/entities/campaign/types';
import {
  generateScheduleSummary,
  validateTimeRanges,
  sortTimeRanges,
} from '../utils/schedulingValidation';
import {
  DEFAULT_SCHEDULING,
  WEEKDAYS,
  markers,
} from '@/features/dooh/campaigns/constants';
import { TimePicker } from '@/shared/ui';
import type { ScreenOnTime } from '@/entities/screen';

interface DaypartingTimepartingProps {
  formik: FormikProps;
}

const DaypartingTimeparting: React.FC<DaypartingTimepartingProps> = React.memo(
  ({ formik }) => {
    const generateId = () => Math.random().toString(36).substr(2, 9);

    const TimelineBar = React.memo(({ ranges }: { ranges: TimeRange[] }) => {
      const theme = useTheme();

      const timeToPercentage = (timeStr: string) => {
        if (!timeStr) return 0;
        const [hours, minutes] = timeStr.split(':').map(Number);
        return ((hours * 60 + minutes) / (24 * 60)) * 100;
      };

      return (
        <Box sx={{ width: '100%', mb: 4, mt: 1 }}>
          <Box
            sx={{
              position: 'relative',
              height: 24,
              backgroundColor: theme.palette.error.main,
              borderRadius: 1,
              overflow: 'hidden',
              opacity: 0.85,
            }}
          >
            {ranges.map((range, index) => {
              if (!range.start || !range.end) return null;

              const startPct = timeToPercentage(range.start);
              const endPct = timeToPercentage(range.end);

              // Normal range
              if (startPct < endPct) {
                return (
                  <Box
                    key={range.id || index}
                    sx={{
                      position: 'absolute',
                      left: `${startPct}%`,
                      width: `${endPct - startPct}%`,
                      height: '100%',
                      backgroundColor: theme.palette.success.main,
                    }}
                  />
                );
              }

              // Overnight range (e.g. 22:00 → 02:00)
              return (
                <React.Fragment key={range.id || index}>
                  {/* Start time → midnight */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${startPct}%`,
                      width: `${100 - startPct}%`,
                      height: '100%',
                      backgroundColor: theme.palette.success.main,
                    }}
                  />

                  {/* Midnight → end time */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '0%',
                      width: `${endPct}%`,
                      height: '100%',
                      backgroundColor: theme.palette.success.main,
                    }}
                  />
                </React.Fragment>
              );
            })}
          </Box>

          <Box
            sx={{ position: 'relative', width: '100%', height: 20, mt: 0.5 }}
          >
            {markers.map(hour => {
              let transform = 'translateX(-50%)';
              if (hour === 0) transform = 'translateX(0)';
              if (hour === 24) transform = 'translateX(-100%)';

              return (
                <Typography
                  key={hour}
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    left: `${(hour / 24) * 100}%`,
                    transform,
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                  }}
                >
                  {hour.toString().padStart(2, '0')}:00
                </Typography>
              );
            })}
          </Box>
        </Box>
      );
    });
    const theme = useTheme();

    // Default values to prevent undefined errors
    const schedule: ScheduleState = useMemo(() => {
      return formik.values.scheduling || DEFAULT_SCHEDULING;
    }, [formik.values.scheduling]);

    const setScheduleField = useCallback(
      (field: keyof ScheduleState, value: any) => {
        const updatedSchedule = { ...schedule, [field]: value };
        formik.setField('scheduling', updatedSchedule);
      },
      [formik, schedule]
    );

    const handleEnableDayParting = (e: React.ChangeEvent<HTMLInputElement>) => {
      setScheduleField('enableDayParting', e.target.checked);
    };

    const handleEnableTimeParting = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      setScheduleField('enableTimeParting', e.target.checked);
    };

    const handleToggleDay = (day: Weekday) => {
      const current = schedule.selectedDays || [];
      const updated = current.includes(day)
        ? current.filter(d => d !== day)
        : [...current, day];
      setScheduleField('selectedDays', updated);
    };

    const handleSelectAllDays = () => {
      setScheduleField(
        'selectedDays',
        WEEKDAYS.map(d => d.id)
      );
    };

    const handleClearDays = () => {
      setScheduleField('selectedDays', []);
    };

    // Generic time range updater
    const handleTimeRangesUpdate = useCallback(
      (targetPath: 'allDays' | Weekday, ranges: TimeRange[]) => {
        const sorted = sortTimeRanges(ranges);

        if (targetPath === 'allDays') {
          setScheduleField('allDays', sorted);
        } else {
          const perDay = { ...(schedule.perDaySchedules || {}) };
          perDay[targetPath] = {
            ...perDay[targetPath],
            enabled: true,
            ranges: sorted,
          };
          setScheduleField('perDaySchedules', perDay);
        }
      },
      [schedule, setScheduleField]
    );

    const handleAddRange = (targetPath: 'allDays' | Weekday) => {
      const currentRanges =
        targetPath === 'allDays'
          ? schedule.allDays || []
          : schedule.perDaySchedules?.[targetPath]?.ranges || [];

      if (currentRanges.length >= 10) return;

      const updated = [
        ...currentRanges,
        { id: generateId(), start: '', end: '' },
      ];
      handleTimeRangesUpdate(targetPath, updated);
    };

    const handleRemoveRange = (
      targetPath: 'allDays' | Weekday,
      rangeId: string
    ) => {
      const currentRanges =
        targetPath === 'allDays'
          ? schedule.allDays || []
          : schedule.perDaySchedules?.[targetPath]?.ranges || [];

      const updated = currentRanges.filter(r => r.id !== rangeId);
      handleTimeRangesUpdate(targetPath, updated);
    };

    const splitOvernightTimeRange = useCallback(
      (
        targetPath: 'allDays' | Weekday,
        rangeId: string,
        ranges: TimeRange[]
      ) => {
        const range = ranges.find(r => r.id === rangeId);
        if (!range || !range.start || !range.end) {
          return;
        }

        if (range.end === '23:59') {
          return;
        }

        if (targetPath !== 'allDays' && range.start > range.end) {
          const originalEnd = range.end;
          const updatedCurrentRanges = ranges.map(r =>
            r.id === rangeId ? { ...r, end: '23:59' } : r
          );

          const currentDayIndex = WEEKDAYS.findIndex(w => w.id === targetPath);
          const nextDayIndex = (currentDayIndex + 1) % 7;
          const nextDay = WEEKDAYS[nextDayIndex].id;

          const perDay = { ...(schedule.perDaySchedules || {}) };
          const updatedPerDay = { ...perDay };

          updatedPerDay[targetPath] = {
            ...updatedPerDay[targetPath],
            enabled: true,
            ranges: sortTimeRanges(updatedCurrentRanges),
          };

          const nextDayRanges = updatedPerDay[nextDay]?.ranges || [];

          const alreadyExists = nextDayRanges.some(
            r => r.start === '00:00' && r.end === originalEnd
          );

          if (alreadyExists) {
            return;
          }

          updatedPerDay[nextDay] = {
            ...updatedPerDay[nextDay],
            enabled: true,
            ranges: sortTimeRanges([
              ...nextDayRanges,
              {
                id: generateId(),
                start: '00:00',
                end: originalEnd,
              },
            ]),
          };

          let selectedDays = schedule.selectedDays || [];
          if (schedule.enableDayParting && !selectedDays.includes(nextDay)) {
            selectedDays = [...selectedDays, nextDay];
          }

          formik.setField('scheduling', {
            ...schedule,
            selectedDays,
            perDaySchedules: updatedPerDay,
          });
        }
      },
      [schedule, formik]
    );

    const handleRangeTimeframeChange = (
      targetPath: 'allDays' | Weekday,
      rangeId: string,
      newTimeframe: ScreenOnTime | null
    ) => {
      if (!newTimeframe) return;

      const start = `${newTimeframe.from.hours}:${newTimeframe.from.minutes}`;
      const end = `${newTimeframe.to.hours}:${newTimeframe.to.minutes}`;

      const currentRanges =
        targetPath === 'allDays'
          ? schedule.allDays || []
          : schedule.perDaySchedules?.[targetPath]?.ranges || [];

      const updatedRanges = currentRanges.map(r =>
        r.id === rangeId ? { ...r, start, end } : r
      );

      handleTimeRangesUpdate(targetPath, updatedRanges);
      splitOvernightTimeRange(targetPath, rangeId, updatedRanges);
    };

    const handleTogglePerDayCustom = (day: Weekday) => {
      const perDay = { ...(schedule.perDaySchedules || {}) };
      const isEnabled = perDay[day]?.enabled;

      perDay[day] = {
        enabled: !isEnabled,
        ranges: perDay[day]?.ranges || [],
      };

      setScheduleField('perDaySchedules', perDay);
    };

    const summaryText = useMemo(
      () => generateScheduleSummary(schedule),
      [schedule]
    );

    const renderTimeRangeBuilder = (
      ranges: TimeRange[],
      targetPath: 'allDays' | Weekday,
      title: string
    ) => {
      const hasAllDaysSlots = (schedule.allDays || []).some(
        r => r.start && r.end
      );

      const hasPerDaySlots = Object.values(schedule.perDaySchedules || {}).some(
        (day: any) =>
          day?.enabled && day?.ranges?.some((r: any) => r.start && r.end)
      );

      const hasAnyScheduleSlots = hasAllDaysSlots || hasPerDaySlots;
      let validationError = validateTimeRanges(ranges);

      if (validationError === 'At least one time slot is required.') {
        validationError = null;
      }

      return (
        <Box sx={{ mt: 2, mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1.5,
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <AccessTimeIcon fontSize="small" />
              {title}
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleAddRange(targetPath)}
              disabled={ranges.length >= 10}
              variant="outlined"
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Add Slot
            </Button>
          </Box>

          {targetPath === 'allDays' && <TimelineBar ranges={ranges} />}

          {validationError &&
            !(
              validationError === 'At least one time slot is required.' &&
              hasAnyScheduleSlots
            ) && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mb: 1, display: 'block' }}
              >
                {validationError}
              </Typography>
            )}

          {ranges.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 2,
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.02)'
                    : 'grey.50',
                borderStyle: 'dashed',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No time slots configured.
              </Typography>
              <Button
                size="small"
                onClick={() => handleAddRange(targetPath)}
                color="primary"
              >
                Add First Slot
              </Button>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {ranges.map(range => (
                <Box
                  key={range.id}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <TimePicker
                      label=""
                      fullWidth
                      timeframe={
                        range.start && range.end
                          ? {
                              from: {
                                hours: range.start.split(':')[0],
                                minutes: range.start.split(':')[1],
                              },
                              to: {
                                hours: range.end.split(':')[0],
                                minutes: range.end.split(':')[1],
                              },
                            }
                          : null
                      }
                      onTimeRangeChange={newTimeframe =>
                        handleRangeTimeframeChange(
                          targetPath,
                          range.id,
                          newTimeframe
                        )
                      }
                    />
                  </Box>
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveRange(targetPath, range.id)}
                    aria-label={`Remove time slot for ${title}`}
                    size="small"
                    sx={{ mt: 1 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      );
    };

    const activeDays = useMemo(() => {
      const days = schedule.enableDayParting
        ? schedule.selectedDays || []
        : WEEKDAYS.map(w => w.id);
      return [...days].sort(
        (a, b) =>
          WEEKDAYS.findIndex(w => w.id === a) -
          WEEKDAYS.findIndex(w => w.id === b)
      );
    }, [schedule.enableDayParting, schedule.selectedDays]);

    return (
      <Accordion
        elevation={2}
        defaultExpanded
        sx={{
          mb: 2,
          mt: 3,
          bgcolor: theme.palette.background.paper,
          borderRadius: '12px !important',
          '&:before': { display: 'none' },
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              width: '100%',
              pr: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: '1.1rem', fontWeight: 600 }}
            >
              Scheduling Options
            </Typography>
            <Chip
              label={summaryText}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ ml: 'auto', fontWeight: 500 }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: { xs: 2, md: 3 }, pt: 0 }}>
          <Divider sx={{ mb: 3 }} />

          {/* Section 1: Day Partitioning */}
          <Box sx={{ mb: 4 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={schedule.enableDayParting}
                  onChange={handleEnableDayParting}
                />
              }
              label={
                <Typography fontWeight={600}>
                  Day Partitioning (Select Specific Days)
                </Typography>
              }
            />

            {schedule.enableDayParting && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  mt: 2,
                  borderRadius: 2,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.02)'
                      : 'grey.50',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleSelectAllDays}
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    onClick={handleClearDays}
                  >
                    Clear
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {WEEKDAYS.map(day => {
                    const isSelected = (schedule.selectedDays || []).includes(
                      day.id
                    );
                    return (
                      <Chip
                        key={day.id}
                        label={day.label}
                        onClick={() => handleToggleDay(day.id)}
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        icon={isSelected ? <CalendarMonthIcon /> : undefined}
                        sx={{ borderRadius: 1.5, px: 1 }}
                      />
                    );
                  })}
                </Box>
                {formik.touched.scheduling &&
                  formik.errors.scheduling &&
                  typeof formik.errors.scheduling === 'string' &&
                  formik.errors.scheduling.includes('Day Partitioning') && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 1, display: 'block' }}
                    >
                      {formik.errors.scheduling as string}
                    </Typography>
                  )}
              </Paper>
            )}
          </Box>

          {/* Section 2: Time Partitioning */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={schedule.enableTimeParting}
                  onChange={handleEnableTimeParting}
                />
              }
              label={
                <Typography fontWeight={600}>
                  Time Partitioning (Working Hours)
                </Typography>
              }
            />

            {schedule.enableTimeParting && (
              <Box
                sx={{
                  mt: 2,
                  pl: { xs: 0, sm: 4 },
                  borderLeft: {
                    xs: 'none',
                    sm: `2px solid ${theme.palette.divider}`,
                  },
                }}
              >
                {/* All Days */}
                <Paper
                  variant="outlined"
                  sx={{ p: 2.5, borderRadius: 2, mb: 3 }}
                >
                  {renderTimeRangeBuilder(
                    schedule.allDays || [],
                    'allDays',
                    'All Days (Default Schedule)'
                  )}
                  <Typography variant="caption" color="text.secondary">
                    This schedule will apply to all active days unless
                    overridden below.
                  </Typography>
                </Paper>

                {/* Per-Day Overrides */}
                {activeDays.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      Per-Day Overrides
                    </Typography>

                    {activeDays.map(dayId => {
                      const day = WEEKDAYS.find(w => w.id === dayId)!;
                      const isCustomEnabled =
                        schedule.perDaySchedules?.[day.id as Weekday]
                          ?.enabled || false;
                      const ranges =
                        schedule.perDaySchedules?.[day.id as Weekday]?.ranges ||
                        [];

                      return (
                        <Accordion
                          key={day.id}
                          variant="outlined"
                          expanded={isCustomEnabled}
                          onChange={() =>
                            handleTogglePerDayCustom(day.id as Weekday)
                          }
                          sx={{
                            mb: 1,
                            borderRadius: '8px !important',
                            '&:before': { display: 'none' },
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                width: '100%',
                              }}
                            >
                              <Typography
                                sx={{ fontWeight: isCustomEnabled ? 600 : 400 }}
                              >
                                {day.label}
                              </Typography>
                              {isCustomEnabled && ranges.length > 0 && (
                                <Chip
                                  size="small"
                                  label={`${ranges.length} slots`}
                                  color="secondary"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            {isCustomEnabled &&
                              renderTimeRangeBuilder(
                                ranges,
                                day.id as Weekday,
                                `${day.label} Schedule`
                              )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  }
);

DaypartingTimeparting.displayName = 'DaypartingTimeparting';

export default DaypartingTimeparting;
